import { CSSProperties, PropsWithChildren, useRef } from 'react';
import {
  Dialog as AriaDialog,
  DialogTrigger,
  HeadingContext,
  Modal,
  ModalOverlay,
  useContextProps,
} from 'react-aria-components';
import { useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import CloseIcon from '@/icons/Cross.svg';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';
import { useWebkitVirtualKeyboardHeight } from '@/utils/useWebkitVirtualKeyboardHeight';

import Title from '../Typography/Title';

import './Dialog.css';

const { block, element } = createBEMClasses('dialog');

function DialogTitleSlot({ title }: { title: React.ReactNode }) {
  const ref = useRef<HTMLHeadingElement>(null);

  const [contextProps, refFromContext] = useContextProps({ slot: 'title' }, ref, HeadingContext);

  const { level, ...titleProps } = contextProps as Record<string, unknown> & { level?: number };

  void level; // from RAC slot, Title uses 'as' for heading level

  return (
    <Title ref={refFromContext} size="300" as="h2" {...titleProps}>
      {title}
    </Title>
  );
}

interface DialogWrapperProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  trigger?: React.ReactNode;
  dismissable?: boolean;
}

export const DialogWrapper = ({
  isOpen,
  onOpenChange,
  trigger,
  children,
  dismissable = true,
}: PropsWithChildren<DialogWrapperProps>) => {
  const virtualKeyboardHeight = useWebkitVirtualKeyboardHeight();

  if (trigger) {
    return (
      <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
        {trigger}
        <ModalOverlay
          className={element('overlay')}
          isDismissable={dismissable}
          isKeyboardDismissDisabled={dismissable}
          style={{ '--virtual-keyboard-height': `${virtualKeyboardHeight}px` } as CSSProperties}
        >
          {children}
        </ModalOverlay>
      </DialogTrigger>
    );
  }

  return (
    <ModalOverlay
      className={element('overlay')}
      isDismissable={dismissable}
      isKeyboardDismissDisabled={dismissable}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      style={{ '--virtual-keyboard-height': `${virtualKeyboardHeight}px` } as CSSProperties}
    >
      {children}
    </ModalOverlay>
  );
};

interface DialogProps extends DialogWrapperProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  dismissable?: boolean;
  fullHeight?: boolean;
}

const Dialog = ({
  isOpen,
  onOpenChange,
  children,
  title,
  trigger,
  className,
  style,
  dismissable = true,
  fullHeight,
}: DialogProps) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  return (
    <DialogWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={trigger}
      dismissable={dismissable}
    >
      <Modal
        className={block({ 'full-height': fullHeight }, className)}
        style={{
          ...style,
          width: isMobile ? undefined : style?.width,
          maxWidth: isMobile ? undefined : style?.maxWidth,
        }}
      >
        <AriaDialog>
          {({ close }) => (
            <>
              <header>
                <DialogTitleSlot title={title} />
                <Button
                  className={element('close-button')}
                  onPress={close}
                  ariaLabel={t({ id: 'design-system.dialog.close', message: 'Close modal' })}
                  iconOnly
                  size="medium"
                  variant="tertiary"
                >
                  <CloseIcon className={element('close-icon')} />
                </Button>
              </header>
              {children}
            </>
          )}
        </AriaDialog>
      </Modal>
    </DialogWrapper>
  );
};

export default Dialog;
